/**
 * Markdown → Knowledge `body_document` v1 (#2502, design D5 refined).
 *
 * A release note arrives as markdown written for engineers. A customer surface
 * may not render raw markdown or HTML (C37, exposure rule 5), so the note is
 * turned into the SAME finite AST a knowledge article uses and drawn by
 * `KbArticleBody`. This file is the one converter; a host never grows its own.
 *
 * What it is, deliberately:
 *
 * - **Deterministic.** Pure string work, no clock, no locale-dependent
 *   collation: the same input deep-equals the same output.
 * - **No HTML path.** v1 has no HTML node, and this converter never needs one:
 *   raw HTML tags and comments are DROPPED (the tag, not the words around it),
 *   and a `<script>` / `<style>` element is dropped together with its content.
 *   Entities are left as written. Every surviving character reaches the page as
 *   a React text node, so nothing here can become markup.
 * - **Finite.** Only v1 nodes are produced: heading (h1 → 2, h5/h6 → 4),
 *   paragraph, list (ordered/unordered, nested by indentation), table (GFM),
 *   callout (a blockquote; GitHub's `[!NOTE]`-style alerts pick the tone),
 *   divider, and the inline text / strong / emphasis / line_break. v1 has no
 *   link and no code node, so a link keeps its TEXT (the href is dropped — there
 *   is no safe-href rule in v1 to admit it under), inline code keeps its text
 *   without backticks, a fenced code block becomes a paragraph whose lines are
 *   joined by line breaks, and an image is dropped (a figure needs a governed
 *   asset, which a markdown URL is not).
 * - **Commit and PR lines are dropped.** A release body generated from a git
 *   log carries lines a customer cannot act on: a leading commit hash,
 *   "Merge pull request …", "Merge branch …", "PR #123", `Co-Authored-By:`,
 *   `Signed-off-by:`. Those lines go; a trailing `(#123)` / `(PR #123)` /
 *   `(abc1234)` reference is cut from the end of an otherwise useful line; a
 *   list item that was nothing but references goes with its marker. A hex token
 *   counts as a commit hash only when it is 7–40 characters and mixes digits
 *   and letters — "defaced" and "2026091" are words and numbers, not commits.
 * - **Bounded.** The validator caps nodes and depth; the converter cuts the
 *   document (whole blocks, then list items / table rows) before the node cap
 *   — the note is truncated, never refused — and flattens structure nested deeper
 *   than the depth cap into plain paragraphs, so its output always parses.
 *
 * A heading left with nothing under it (its section was only commit lines) is
 * dropped. When nothing renderable remains, the result is `null`: a v1 document
 * must have at least one block, and inventing an empty paragraph would draw a
 * blank note. The caller shows its own empty state.
 */
import { parseKbDocument, type KbDocumentBlock, type KbDocumentV1, type KbInlineNode } from './document'

/* The validator's own ceilings (document.ts MAX_NODES / MAX_DEPTH), kept a
   margin inside so a converted document is never the one that trips them. */
const NODE_BUDGET = 9_000
const MAX_BLOCK_NESTING = 5

const HEX_HASH = /^(?=[0-9a-f]*[0-9])(?=[0-9a-f]*[a-f])[0-9a-f]{7,40}$/
const REF = String.raw`(?:(?:PR|pull request)\s*)?#\d+`
const TRAILING_REFS = new RegExp(String.raw`\s*\(\s*${REF}(?:\s*,\s*${REF})*\s*\)\s*$`, 'i')
const TRAILING_PR = /\s*[-–—]?\s*\bPR\s*#\d+\s*$/i
const TRAILING_HASH = /\s*\(\s*([0-9a-f]{7,40})\s*\)\s*$/
const DROP_LINE = /^(?:co-authored-by:|signed-off-by:|merge pull request\b|merge branch\b|merge remote-tracking branch\b|pr\s*#\d+\b)/i
const ONLY_REFS = new RegExp(String.raw`^(?:[(\s,]*(?:${REF}|[0-9a-f]{7,40})[)\s,]*)+$`, 'i')

/** Drops a commit/PR line (returns null) or cuts its trailing references. */
function scrubLine(line: string): string | null {
  const text = line.trim()
  if (!text) return line
  if (DROP_LINE.test(text)) return null
  const lead = text.split(/\s+/u)[0] ?? ''
  if (HEX_HASH.test(lead.replace(/[:)\]]+$/u, ''))) return null
  if (ONLY_REFS.test(text) && (/#\d/u.test(text) || text.split(/[\s(),]+/u).filter(Boolean).every((t) => HEX_HASH.test(t)))) return null
  let out = line
  for (;;) {
    const before = out
    out = out.replace(TRAILING_REFS, '').replace(TRAILING_PR, '')
    const hash = TRAILING_HASH.exec(out)
    if (hash && HEX_HASH.test(hash[1] ?? '')) out = out.slice(0, hash.index)
    if (out === before) break
  }
  return out.trim() ? out : null
}

/** Raw HTML never survives: tags and comments go; script/style go with their body. */
function stripHtml(source: string): string {
  return source
    .replace(/<!--[\s\S]*?(?:-->|$)/gu, '')
    .replace(/<(script|style)\b[^>]*>[\s\S]*?(?:<\/\1\s*>|$)/giu, '')
    .replace(/<(https?:\/\/[^\s<>]+)>/giu, '$1')
    .replace(/<\/?[A-Za-z](?:[^<>"']|"[^"]*"|'[^']*')*>/gu, '')
    .replace(/<![A-Za-z][^<>]*>/gu, '')
    .replace(/<\?[^<>]*\?>/gu, '')
}

/* ── inline ─────────────────────────────────────────────────────────────── */

const ESCAPABLE = /[\\`*_{}[\]()#+\-.!|>~<"']/u

function pushText(out: KbInlineNode[], text: string): void {
  if (!text) return
  const last = out[out.length - 1]
  if (last && last.type === 'text') out[out.length - 1] = { type: 'text', text: last.text + text }
  else out.push({ type: 'text', text })
}

function closingBracket(source: string, open: number): number {
  let depth = 0
  for (let i = open; i < source.length; i += 1) {
    const ch = source[i]
    if (ch === '\\') { i += 1; continue }
    if (ch === '[') depth += 1
    else if (ch === ']') { depth -= 1; if (depth === 0) return i }
  }
  return -1
}

function closingParen(source: string, open: number): number {
  let depth = 0
  for (let i = open; i < source.length; i += 1) {
    const ch = source[i]
    if (ch === '\\') { i += 1; continue }
    if (ch === '(') depth += 1
    else if (ch === ')') { depth -= 1; if (depth === 0) return i }
  }
  return -1
}

function isWordChar(ch: string | undefined): boolean {
  return ch !== undefined && /[\p{L}\p{N}]/u.test(ch)
}

/** A closing delimiter run: not preceded by whitespace; `_` must not be intraword. */
function findClose(source: string, from: number, delim: string): number {
  for (let i = from; i <= source.length - delim.length; i += 1) {
    if (source[i] === '\\') { i += 1; continue }
    if (source[i] === '`') {
      const end = source.indexOf('`', i + 1)
      if (end > 0) { i = end; continue }
    }
    if (source.startsWith(delim, i)
      && !/\s/u.test(source[i - 1] ?? ' ')
      && (delim.length === 2 || source[i + 1] !== delim[0])
      && (delim[0] !== '_' || !isWordChar(source[i + delim.length]))) return i
  }
  return -1
}

function inlineNodes(source: string, depth = 0): KbInlineNode[] {
  const out: KbInlineNode[] = []
  let i = 0
  while (i < source.length) {
    const ch = source[i] ?? ''
    if (ch === '\\' && i + 1 < source.length && ESCAPABLE.test(source[i + 1] ?? '')) {
      pushText(out, source[i + 1] ?? '')
      i += 2
      continue
    }
    if (ch === '`') {
      let run = 1
      while (source[i + run] === '`') run += 1
      const fence = '`'.repeat(run)
      const end = source.indexOf(fence, i + run)
      if (end > i) {
        pushText(out, source.slice(i + run, end).trim())
        i = end + run
        continue
      }
      pushText(out, fence)
      i += run
      continue
    }
    if (ch === '!' && source[i + 1] === '[') {
      const close = closingBracket(source, i + 1)
      if (close > 0 && source[close + 1] === '(') {
        const end = closingParen(source, close + 1)
        if (end > 0) { i = end + 1; continue }
      }
    }
    if (ch === '[') {
      const close = closingBracket(source, i)
      if (close > 0 && source[close + 1] === '(') {
        const end = closingParen(source, close + 1)
        if (end > 0) {
          for (const node of inlineNodes(source.slice(i + 1, close), depth)) {
            if (node.type === 'text') pushText(out, node.text)
            else out.push(node)
          }
          i = end + 1
          continue
        }
      }
    }
    if ((ch === '*' || ch === '_') && depth < 3) {
      const double = source[i + 1] === ch
      const delim = double ? ch + ch : ch
      const opensWord = ch === '_' && isWordChar(source[i - 1])
      const next = source[i + delim.length]
      if (!opensWord && next !== undefined && !/\s/u.test(next)) {
        const close = findClose(source, i + delim.length, delim)
        if (close > i + delim.length) {
          const content = inlineNodes(source.slice(i + delim.length, close), depth + 1)
          if (content.length) {
            out.push({ type: double ? 'strong' : 'emphasis', content })
            i = close + delim.length
            continue
          }
        }
      }
      pushText(out, delim)
      i += delim.length
      continue
    }
    pushText(out, ch)
    i += 1
  }
  return out
}

/** One paragraph's lines: soft breaks become spaces, hard breaks line_break. */
function paragraphInline(lines: string[]): KbInlineNode[] {
  const out: KbInlineNode[] = []
  lines.forEach((raw, index) => {
    const hard = index < lines.length - 1 && (/ {2,}$/u.test(raw) || /\\$/u.test(raw))
    const text = raw.replace(/\\$/u, '').trim()
    for (const node of inlineNodes(text)) {
      if (node.type === 'text') pushText(out, node.text)
      else out.push(node)
    }
    if (index < lines.length - 1) {
      if (hard) out.push({ type: 'line_break' })
      else pushText(out, ' ')
    }
  })
  return trimInline(out)
}

function trimInline(nodes: KbInlineNode[]): KbInlineNode[] {
  const out = [...nodes]
  const first = out[0]
  if (first?.type === 'text') out[0] = { type: 'text', text: first.text.trimStart() }
  const lastIndex = out.length - 1
  const last = out[lastIndex]
  if (last?.type === 'text') out[lastIndex] = { type: 'text', text: last.text.trimEnd() }
  const kept = out.filter((node) => node.type !== 'text' || node.text.length > 0)
  while (kept[0]?.type === 'line_break') kept.shift()
  while (kept.at(-1)?.type === 'line_break') kept.pop()
  return kept
}

/* ── blocks ─────────────────────────────────────────────────────────────── */

const FENCE = /^ {0,3}(`{3,}|~{3,})/u
const HEADING = /^ {0,3}(#{1,6})(?:\s+(.*?))?\s*#*\s*$/u
const RULE = /^ {0,3}(?:(?:\*\s*){3,}|(?:-\s*){3,}|(?:_\s*){3,})$/u
const QUOTE = /^ {0,3}>\s?/u
const BULLET = /^( *)([-*+])\s+(.*)$/u
const ORDERED = /^( *)(\d{1,9})[.)]\s+(.*)$/u
const TABLE_SEPARATOR = /^\s*\|?\s*:?-+:?\s*(?:\|\s*:?-+:?\s*)*\|?\s*$/u
const ALERT = /^\[!(NOTE|TIP|IMPORTANT|WARNING|CAUTION|INFO)\]\s*$/iu

function indentOf(line: string): number {
  return (/^ */u.exec(line.replace(/\t/gu, '    '))?.[0].length) ?? 0
}

function listMarker(line: string): { indent: number; ordered: boolean; rest: string } | null {
  const expanded = line.replace(/\t/gu, '    ')
  const bullet = BULLET.exec(expanded)
  if (bullet && !RULE.test(expanded)) return { indent: bullet[1]?.length ?? 0, ordered: false, rest: bullet[3] ?? '' }
  const ordered = ORDERED.exec(expanded)
  if (ordered) return { indent: ordered[1]?.length ?? 0, ordered: true, rest: ordered[3] ?? '' }
  return null
}

function tableCells(line: string): string[] {
  let body = line.trim()
  if (body.startsWith('|')) body = body.slice(1)
  if (body.endsWith('|') && !body.endsWith('\\|')) body = body.slice(0, -1)
  const cells: string[] = []
  let current = ''
  for (let i = 0; i < body.length; i += 1) {
    if (body[i] === '\\' && body[i + 1] === '|') { current += '|'; i += 1; continue }
    if (body[i] === '|') { cells.push(current.trim()); current = ''; continue }
    current += body[i]
  }
  cells.push(current.trim())
  return cells
}

function cellInline(text: string): KbInlineNode[] {
  const nodes = trimInline(inlineNodes(text))
  return nodes.length ? nodes : [{ type: 'text', text: '—' }]
}

function isBlockStart(line: string): boolean {
  return FENCE.test(line) || HEADING.test(line) || RULE.test(line) || QUOTE.test(line) || listMarker(line) !== null
}

function flatten(blocks: KbDocumentBlock[]): KbDocumentBlock[] {
  const out: KbDocumentBlock[] = []
  for (const item of blocks) {
    if (item.type === 'list') for (const entry of item.items) out.push(...flatten(entry.blocks))
    else if (item.type === 'callout') out.push(...flatten(item.blocks))
    else out.push(item)
  }
  return out
}

function parseBlocks(lines: string[], nesting: number): KbDocumentBlock[] {
  const out: KbDocumentBlock[] = []
  let i = 0
  while (i < lines.length) {
    const line = lines[i] ?? ''
    if (!line.trim()) { i += 1; continue }

    const fence = FENCE.exec(line)
    if (fence) {
      const marker = fence[1] ?? '```'
      const body: string[] = []
      i += 1
      while (i < lines.length && !(lines[i] ?? '').trim().startsWith(marker)) {
        body.push(lines[i] ?? '')
        i += 1
      }
      i += 1
      const content: KbInlineNode[] = []
      body.forEach((codeLine, index) => {
        if (index > 0) content.push({ type: 'line_break' })
        if (codeLine) content.push({ type: 'text', text: codeLine })
      })
      const trimmed = trimInline(content)
      if (trimmed.length) out.push({ type: 'paragraph', content: trimmed })
      continue
    }

    const heading = HEADING.exec(line)
    if (heading) {
      const content = trimInline(inlineNodes(heading[2] ?? ''))
      const rank = heading[1]?.length ?? 2
      if (content.length) out.push({ type: 'heading', level: Math.min(4, Math.max(2, rank)) as 2 | 3 | 4, content })
      i += 1
      continue
    }

    if (RULE.test(line)) { out.push({ type: 'divider' }); i += 1; continue }

    if (QUOTE.test(line)) {
      const body: string[] = []
      while (i < lines.length && QUOTE.test(lines[i] ?? '')) {
        body.push((lines[i] ?? '').replace(QUOTE, ''))
        i += 1
      }
      const alert = ALERT.exec((body[0] ?? '').trim())
      const tone = alert
        ? ({ NOTE: 'note', INFO: 'info', TIP: 'info', IMPORTANT: 'info', WARNING: 'warning', CAUTION: 'warning' } as const)[
          (alert[1] ?? 'NOTE').toUpperCase() as 'NOTE']
        : 'note'
      const inner = parseBlocks(alert ? body.slice(1) : body, nesting + 1)
      if (nesting >= MAX_BLOCK_NESTING) out.push(...flatten(inner))
      else if (inner.length) out.push({ type: 'callout', tone, blocks: inner })
      continue
    }

    const marker = listMarker(line)
    if (marker) {
      const items: Array<{ blocks: KbDocumentBlock[] }> = []
      while (i < lines.length) {
        const current = listMarker(lines[i] ?? '')
        if (!current || current.ordered !== marker.ordered || current.indent !== marker.indent) break
        const body = [current.rest]
        const contentIndent = marker.indent + 2
        i += 1
        while (i < lines.length) {
          const next = lines[i] ?? ''
          if (!next.trim()) {
            const after = lines[i + 1] ?? ''
            if (after.trim() && indentOf(after) >= contentIndent) { body.push(''); i += 1; continue }
            break
          }
          if (indentOf(next) >= contentIndent) {
            body.push(next.replace(/\t/gu, '    ').slice(Math.min(indentOf(next), contentIndent)))
            i += 1
            continue
          }
          if (!isBlockStart(next) && indentOf(next) > marker.indent) { body.push(next.trim()); i += 1; continue }
          break
        }
        const itemBlocks = parseBlocks(body, nesting + 1)
        if (itemBlocks.length) items.push({ blocks: nesting >= MAX_BLOCK_NESTING ? flatten(itemBlocks) : itemBlocks })
        while (i < lines.length && !(lines[i] ?? '').trim()) {
          const after = listMarker(lines[i + 1] ?? '')
          if (after && after.ordered === marker.ordered && after.indent === marker.indent) i += 1
          else break
        }
      }
      if (items.length) {
        if (nesting >= MAX_BLOCK_NESTING) out.push(...items.flatMap((item) => item.blocks))
        else out.push({ type: 'list', ordered: marker.ordered, items })
      }
      continue
    }

    if (line.includes('|') && TABLE_SEPARATOR.test(lines[i + 1] ?? '') && (lines[i + 1] ?? '').includes('-')) {
      const header = tableCells(line)
      const rows: string[][] = []
      i += 2
      while (i < lines.length && (lines[i] ?? '').trim() && (lines[i] ?? '').includes('|')) {
        rows.push(tableCells(lines[i] ?? ''))
        i += 1
      }
      if (rows.length) {
        const width = header.length
        const fit = (cells: string[]) => Array.from({ length: width }, (_, index) => cellInline(cells[index] ?? ''))
        out.push({ type: 'table', header: fit(header), rows: rows.map(fit) })
      } else {
        const content = trimInline(inlineNodes(line))
        if (content.length) out.push({ type: 'paragraph', content })
      }
      continue
    }

    const paragraph: string[] = []
    while (i < lines.length && (lines[i] ?? '').trim() && (paragraph.length === 0 || !isBlockStart(lines[i] ?? ''))) {
      paragraph.push(lines[i] ?? '')
      i += 1
    }
    const content = paragraphInline(paragraph)
    if (content.length) out.push({ type: 'paragraph', content })
  }
  return dropEmptySections(out)
}

/** A heading whose section holds nothing (every line in it was a commit line) is noise. */
function dropEmptySections(blocks: KbDocumentBlock[]): KbDocumentBlock[] {
  let current = blocks
  for (;;) {
    const kept = current.filter((item, index) => {
      if (item.type !== 'heading') return true
      const next = current[index + 1]
      return next !== undefined && !(next.type === 'heading' && next.level <= item.level)
    })
    if (kept.length === current.length) return kept
    current = kept
  }
}

function countInline(nodes: KbInlineNode[]): number {
  return nodes.reduce((sum, node) => sum + 1 + (node.type === 'strong' || node.type === 'emphasis' ? countInline(node.content) : 0), 0)
}

function countBlock(item: KbDocumentBlock): number {
  switch (item.type) {
    case 'heading':
    case 'paragraph': return 1 + countInline(item.content)
    case 'figure': return 1 + countInline(item.caption)
    case 'divider': return 1
    case 'callout': return 1 + item.blocks.reduce((sum, child) => sum + countBlock(child), 0)
    case 'list': return 1 + item.items.reduce((sum, entry) => sum + entry.blocks.reduce((inner, child) => inner + countBlock(child), 0), 0)
    case 'table': return 1 + [item.header, ...item.rows].reduce((sum, row) => sum + row.reduce((cells, cell) => cells + countInline(cell), 0), 0)
  }
}

/** The block cut to fit `room` nodes — a list keeps its first items, a table
 * its first rows, a callout its first blocks — or null when not even that fits. */
function fit(item: KbDocumentBlock, room: number): KbDocumentBlock | null {
  if (countBlock(item) <= room) return item
  if (item.type === 'list') {
    const items: Array<{ blocks: KbDocumentBlock[] }> = []
    let left = room - 1
    for (const entry of item.items) {
      const kept = fitAll(entry.blocks, left)
      if (!kept.length) break
      items.push({ blocks: kept })
      left -= kept.reduce((sum, child) => sum + countBlock(child), 0)
      if (kept.length < entry.blocks.length) break
    }
    return items.length ? { ...item, items } : null
  }
  if (item.type === 'callout') {
    const blocks = fitAll(item.blocks, room - 1)
    return blocks.length ? { ...item, blocks } : null
  }
  if (item.type === 'table') {
    const rows: KbInlineNode[][][] = []
    let left = room - 1 - item.header.reduce((sum, cell) => sum + countInline(cell), 0)
    for (const row of item.rows) {
      const cost = row.reduce((sum, cell) => sum + countInline(cell), 0)
      if (cost > left) break
      rows.push(row)
      left -= cost
    }
    return rows.length ? { ...item, rows } : null
  }
  return null
}

function fitAll(blocks: KbDocumentBlock[], room: number): KbDocumentBlock[] {
  const out: KbDocumentBlock[] = []
  let left = room
  for (const item of blocks) {
    const kept = fit(item, left)
    if (!kept) break
    out.push(kept)
    left -= countBlock(kept)
    if (kept !== item) break
  }
  return out
}

/**
 * Converts release-note markdown to a `body_document` v1, or `null` when
 * nothing renderable remains. The result always passes `parseKbDocument`.
 */
export function markdownToKbDocument(markdown: string): KbDocumentV1 | null {
  const lines: string[] = []
  for (const raw of stripHtml(String(markdown ?? '')).replace(/\r\n?/gu, '\n').split('\n')) {
    const quote = /^((?: {0,3}>\s?)*)/u.exec(raw)?.[1] ?? ''
    const rest = raw.slice(quote.length)
    const item = /^(\s*(?:[-*+]|\d{1,9}[.)])\s+)(.*)$/u.exec(rest)
    const prefix = quote + (item ? item[1] ?? '' : '')
    const body = item ? item[2] ?? '' : rest
    if (!body.trim()) {
      if (!item) lines.push(raw)
      continue
    }
    const scrubbed = scrubLine(body)
    if (scrubbed === null) continue
    lines.push(prefix + scrubbed)
  }

  const blocks = fitAll(parseBlocks(lines, 0), NODE_BUDGET)
  if (!blocks.length) return null
  const document: KbDocumentV1 = { version: 1, blocks }
  /* The finite validator is the arbiter, not this file's intentions: a result
     it would refuse is not returned. */
  return parseKbDocument(document) ? document : null
}
